import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { LocationService } from 'src/app/core/services/location.service';
import { AppService } from 'src/app/core';
import { PLATFORM } from 'src/app/constants';
import { DOCUMENT } from '@angular/common';
import { CouponService } from '../../views/coupons/coupons.service';
import {
  Location as CustomLocation,
  PincodeResponse
} from '../../views/coupons/interfaces';
@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: 'app-location-popup',
  templateUrl: './location-popup.component.html',
  styleUrl: './location-popup.component.scss'
})
export class LocationPopupComponent implements OnInit, OnDestroy {
  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();
  searchLocation = new FormControl('');
  locations: CustomLocation[] = [];
  pincodeResult: PincodeResponse | null = null;
  noSearchResults = false;
  loader = false;
  isLocationAllowed = false;

  // Push a history entry while the popup is open so the WebView's System
  // Back CTA pops that entry (closing the popup) instead of navigating off
  // the page. closingFromPopstate distinguishes "browser already consumed
  // the entry" from "we need to call history.back() ourselves".
  private hasPushedHistoryEntry = false;
  private closingFromPopstate = false;
  private readonly popstateHandler = () => {
    if (this.showModal) {
      this.closingFromPopstate = true;
      this.locationService.toggleModal(false);
    }
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private couponService: CouponService,
    private locationService: LocationService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.searchLocation.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((val) => {
        if (!val) {
          this.locations = [];
          this.pincodeResult = null;
          this.noSearchResults = false;
          return;
        }
        this.locations = [];
        this.pincodeResult = null;
        // 6-digit numeric input -> pin code lookup; everything else -> autocomplete.
        if (/^\d{6}$/.test(val)) {
          this.searchPincode(val);
        } else {
          this.getLocationOptions(val);
        }
      });
    this.closeModal();
    this.showLocationModal();
    window.addEventListener('popstate', this.popstateHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popstateHandler);
  }

  // Single source of truth for opening/closing. Keeps body scroll-lock and
  // browser history in sync. Idempotent — calling with the current value
  // is a no-op.
  private setShown(shown: boolean): void {
    if (shown === this.showModal) {
      return;
    }
    this.showModal = shown;

    if (shown) {
      this.renderer.addClass(this.document.body, 'no-scroll');
      history.pushState({ locationPopup: true }, '');
      this.hasPushedHistoryEntry = true;
    } else {
      this.renderer.removeClass(this.document.body, 'no-scroll');
      if (this.hasPushedHistoryEntry) {
        this.hasPushedHistoryEntry = false;
        if (this.closingFromPopstate) {
          // Browser already popped the entry for us.
          this.closingFromPopstate = false;
        } else {
          history.back();
        }
      }
    }
  }

  handleSearch($event: Event) {
    $event?.preventDefault();
    $event?.stopPropagation();
    $event?.stopImmediatePropagation();
  }

  getLocationOptions(input: string) {
    this.loader = true;
    this.couponService.getLocations(input).subscribe({
      next: (res) => {
        this.locations = res.predictions;
        this.loader = false;
        this.noSearchResults = !this.locations.length;
      },
      error: () => {
        this.loader = false;
      }
    });
  }

  searchPincode(pincode: string) {
    this.loader = true;
    this.couponService.searchPincode(pincode).subscribe({
      next: (res) => {
        if (res && (res.district || res.state)) {
          this.pincodeResult = res;
          this.noSearchResults = false;
        } else {
          this.pincodeResult = null;
          this.noSearchResults = true;
        }
        this.loader = false;
      },
      error: () => {
        this.pincodeResult = null;
        this.noSearchResults = true;
        this.loader = false;
      }
    });
  }

  selectLocation(location: CustomLocation) {
    this.locationService.setLocation(location.structured_formatting.main_text);
    this.locationService.toggleModal(false);
    this.setShown(false);
    this.locations = [];
  }

  selectPincode(result: PincodeResponse) {
    // Pincode goes to appService so coupons.component reads it for the filter.
    this.appService.setPincode(result.pincode);
    const display = result.district
      ? `${result.district}${result.state ? ', ' + result.state : ''}`
      : result.pincode;
    this.locationService.setLocation(display);
    this.locationService.toggleModal(false);
    this.setShown(false);
    this.pincodeResult = null;
  }

  useCurrentLocation() {
    this.loader = true;

    if (this.appService.getPlatform() === PLATFORM.NATIVE_APP) {
      const coords = this.locationService.getCoordinates();
      if (coords) {
        this.couponService
          .getGeoCode(coords.lat, coords.lon)
          .subscribe((res) => {
            this.locationService.setLocation(res.plus_code.compound_code);
            this.locationService.setHasNoLocation(false);
            this.loader = false;
          });
        return;
      }
    }

    this.locationService.getCurrentLocation().subscribe(
      () => {
        this.loader = false;
      },
      (error: GeolocationPositionError) => {
        this.loader = false;
        const { code, message } = error;
        if (code === 1 && message === 'User denied Geolocation') {
          this.isLocationAllowed = true;
        }
      }
    );
  }

  clearSearchText() {
    this.searchLocation.setValue('');
    this.loader = false;
    this.locations = [];
  }

  closeModal() {
    this.locationService.location$.subscribe((loc) => {
      if (loc) {
        this.setShown(false);
        this.loader = false;
        this.clearSearchText();
        this.isLocationAllowed = false;
        this.locationService.setHasNoLocation(false);
        this.showModalChange.emit(this.showModal);
      }
    });
  }

  showLocationModal() {
    this.locationService.showModal$.subscribe((modal) => {
      this.setShown(!!modal);
    });
  }

  closePopup() {
    this.setShown(false);
    this.showModalChange.emit(this.showModal);
  }
}
